import { useState, useEffect, useRef } from "react";
import CityPicker from "./CityPicker";
import { searchEnterprises } from "@/services/dataserver";

interface SearchFilterProps {
  city: string;
  onCityChange: (city: string) => void;
  onLocationSelect?: (lon: number, lat: number, name: string, data?: Record<string, any>) => void;
}

interface EnterpriseResult {
  name: string;
  addr: string;
  lon: number;
  lat: number;
  gid?: string | number;
  _raw?: Record<string, any>;
  [key: string]: any;
}

export default function SearchFilter({ city, onCityChange, onLocationSelect }: SearchFilterProps) {
  const [keyword, setKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<EnterpriseResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState<EnterpriseResult | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const list = await searchEnterprises(keyword.trim());
      if (!Array.isArray(list)) {
        setSearchResults([]);
        return;
      }
      const mapped = list.map((item: any) => ({
        name: item.name || item.NAME || item.qymc || '未知',
        addr: item.addr || item.ADDR || item.address || '',
        lon: parseFloat(item.lon || item.LON || 0),
        lat: parseFloat(item.lat || item.LAT || 0),
        gid: item.gid || item.GID || item.id || item.ID,
        _raw: item,
      }));
      setSearchResults(mapped);
    } catch (error) {
      console.error('企业搜索失败:', error);
      setSearchResults([]);
    }
  };

  // 当关键词变化时进行搜索（防抖）
  useEffect(() => {
    if (!keyword.trim()) {
      setSearchResults([]);
      setSelectedResult(null);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [keyword]);

  // 处理选择搜索结果
  const handleSelectResult = (result: EnterpriseResult) => {
    setKeyword(result.name);
    setSelectedResult(result);
    setShowResults(false);

    if (result.lon && result.lat) {
      onLocationSelect?.(result.lon, result.lat, result.name, result._raw || result);
    }
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setKeyword(value);
    setSelectedResult(null);
    if (!value.trim()) {
      setSearchResults([]);
      setShowResults(false);
      if (onLocationSelect) {
        onLocationSelect(0, 0, '');
      }
    } else {
      setShowResults(true);
    }
  };

  // 处理输入框焦点 - 显示下拉框
  const handleInputFocus = () => {
    if (keyword.trim()) {
      setShowResults(true);
    }
  };

  // 点击搜索按钮 - 传递选中的位置数据给地图
  const handleManualSearch = () => {
    if (selectedResult && onLocationSelect) {
      onLocationSelect(selectedResult.lon, selectedResult.lat, selectedResult.name, selectedResult._raw || selectedResult);
    } else if (searchResults.length > 0 && onLocationSelect) {
      const firstResult = searchResults[0];
      onLocationSelect(firstResult.lon, firstResult.lat, firstResult.name, firstResult._raw || firstResult);
    }
  };

  return (
    <div className="absolute top-20 left-4 z-[1000] flex items-center gap-2">
      {/* Search 容器 */}
      <div className="relative" ref={searchContainerRef}>
        <div className="flex items-center bg-white/92 backdrop-blur-md border border-slate-200/80 rounded-lg overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          <i className="ri-search-line text-slate-400 text-sm ml-3"></i>
          <div className="relative flex items-center">
            <input
              ref={inputRef}
              type="text"
              value={keyword}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder="搜索企业..."
              className="bg-transparent text-slate-800 placeholder-slate-400 text-sm pl-3 pr-6 py-2.5 outline-none w-56"
            />
            {keyword && (
              <button
                onClick={() => { setKeyword(""); setShowResults(false); onLocationSelect?.(0, 0, ''); inputRef.current?.focus(); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full bg-slate-300/60 hover:bg-slate-400/70 text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-[11px]" />
              </button>
            )}
          </div>
          <button
            onClick={handleManualSearch}
            className="px-3 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>

        {/* 搜索结果下拉列表 */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute top-full left-0 mt-1 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-lg shadow-lg max-h-60 overflow-y-auto z-[2000] w-full min-w-[224px]">
            {searchResults.map((result, idx) => (
              <div
                key={result.gid || idx}
                onClick={() => handleSelectResult(result)}
                className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm border-b border-slate-100 last:border-b-0"
              >
                <div className="font-medium text-slate-800">{result.name}</div>
                {result.addr && (
                  <div className="text-slate-500 text-xs mt-1">{result.addr}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 无搜索结果提示 */}
        {showResults && searchResults.length === 0 && keyword.trim() && (
          <div className="absolute top-full left-0 mt-1 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-lg shadow-lg p-3 text-slate-500 text-sm z-[2000] w-full min-w-[224px]">
            未找到相关企业
          </div>
        )}
      </div>

      {/* City Picker */}
      <div style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }} className="rounded-lg z-[1000]">
        <CityPicker city={city} onSelect={onCityChange} />
      </div>

      {/* 重置地图按钮 */}
      <button
        onClick={() => window.location.reload()}
        className="flex items-center gap-2 px-3 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap rounded-lg"
        style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
      >
        <i className="ri-refresh-line text-sm"></i>
        重置地图
      </button>
    </div>
  );
}